
import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';

type AuthContextType = {
  session: Session | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{
    error: Error | null;
  }>;
  signOut: () => Promise<void>;
  isCourseOwner: boolean;
  updatePassword: (password: string) => Promise<{
    error: Error | null;
  }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCourseOwner, setIsCourseOwner] = useState(false);

  useEffect(() => {
    // Set up auth state listener FIRST to ensure we catch all auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event, session);
        setSession(session);
        
        // Update course owner status when session changes
        if (session?.user?.email) {
          const isOwner = 
            session.user.email.endsWith('@thinktomorrow.ai') || 
            session.user.email === 'admin@example.com';
          setIsCourseOwner(isOwner);
        } else {
          setIsCourseOwner(false);
        }
        
        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Retrieved session:', session);
      setSession(session);
      
      // Check if user is a course owner based on email domain or specific emails
      if (session?.user?.email) {
        const isOwner = 
          session.user.email.endsWith('@thinktomorrow.ai') || 
          session.user.email === 'admin@example.com';
        setIsCourseOwner(isOwner);
      }
      
      setIsLoading(false);
      
      // Try to load session from localStorage for test users
      if (!session) {
        const savedSession = localStorage.getItem('testUserSession');
        if (savedSession) {
          try {
            const parsedSession = JSON.parse(savedSession);
            setSession(parsedSession);
            setIsLoading(false);
          } catch (e) {
            console.error('Error parsing saved session:', e);
          }
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      // Special case for test@example.com to avoid actual Supabase auth
      if (email === 'test@example.com' && password === 'password123') {
        console.log('Using test credentials - bypassing Supabase auth');
        
        // Create a mock session for test user
        const mockSession = {
          access_token: 'test-token',
          refresh_token: 'test-refresh-token',
          expires_in: 3600,
          expires_at: new Date().getTime() + 3600000,
          token_type: 'bearer',
          user: {
            id: 'test-user-id',
            app_metadata: {},
            user_metadata: { full_name: 'Test User' },
            aud: 'authenticated',
            email: 'test@example.com',
            role: 'authenticated',
            email_confirmed_at: new Date().toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            confirmed_at: new Date().toISOString(),
            last_sign_in_at: new Date().toISOString(),
            factors: null,
            phone: null,
            phone_confirmed_at: null,
            identities: [],
          }
        } as Session;
        
        // Set the mock session
        setSession(mockSession);
        setIsCourseOwner(false);
        
        // Save test session to localStorage to persist across page reloads
        localStorage.setItem('testUserSession', JSON.stringify(mockSession));
        
        // Return success without error
        return { error: null };
      }

      // Special case for course owners
      if (email.endsWith('@thinktomorrow.ai') || email === 'admin@example.com') {
        setIsCourseOwner(true);
      }
      
      // Ensure we use the correct options for auth persistence
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      return { error };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    // Clear any test user session from localStorage
    localStorage.removeItem('testUserSession');
    
    await supabase.auth.signOut();
    
    // Also clear any mock session if we're in test mode
    if (session?.user?.email === 'test@example.com') {
      setSession(null);
    }
    
    setIsCourseOwner(false);
  };

  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      return { error };
    } catch (error) {
      console.error('Update password error:', error);
      return { error: error as Error };
    }
  };

  return (
    <AuthContext.Provider value={{ 
      session, 
      isLoading, 
      signIn, 
      signOut, 
      isCourseOwner,
      updatePassword 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
